import React from 'react';
import themedAssetsConfig from '~/../themed-assets.config.json';
import { useTheme } from '~/components/Providers/ThemeContext';

type ThemedAssetConfig = {
    source: string;
    outputName?: string;
    baseHue?: number;
};

const themedAssetMap = new Map<string, ThemedAssetConfig>(
    themedAssetsConfig.assets.map((asset) => [asset.source, asset]),
);

const highestHue = 360 - themedAssetsConfig.hueStep;
const huePadLength = String(highestHue).length;

const getOutputName = (assetConfig: ThemedAssetConfig, source: string) => {
    if (assetConfig.outputName) return assetConfig.outputName;
    const trimmed = source.startsWith('/') ? source.slice(1) : source;
    const withoutExtension = trimmed
        .replace(/^assets\//, '')
        .replace(/\.[^.]+$/, '');
    return withoutExtension;
};

interface HueRotateImageProps {
    src: string;
    hueRotation: number;
    lightnessCorrection?: number;
    saturationCorrection?: number;
    alt?: string;
    className?: string;
    maxWidth?: number;
}

const ThemedImage: React.FC<HueRotateImageProps> = ({
    src,
    hueRotation = 0,
    alt = '',
    className,
    maxWidth,
    lightnessCorrection = 100,
    saturationCorrection = 100,
}) => {
    const { isDefaultColor } = useTheme();

    const source = (() => {
        const assetConfig = themedAssetMap.get(src);
        if (!assetConfig || isDefaultColor) return src;

        const targetHue =
            ((isNaN(hueRotation) ? (assetConfig.baseHue ?? 0) : hueRotation) +
                360) %
            360;
        const normalizedHue =
            (((Math.round(targetHue / themedAssetsConfig.hueStep) *
                themedAssetsConfig.hueStep) %
                360) +
                360) %
            360;

        const outputName = getOutputName(assetConfig, src);
        const hueKey = String(normalizedHue).padStart(huePadLength, '0');

        return `/themed_assets/${outputName}/${hueKey}.webp`;
    })();

    const saturationFilter = 0.2 + (saturationCorrection / 100) * 0.8;
    const brightnessFilter = Math.min(
        Math.max(0.1, lightnessCorrection / 100),
        1.4,
    );

    return (
        <img
            src={source}
            alt={alt}
            className={className}
            style={{
                maxWidth:
                    typeof maxWidth === 'number' ? `${maxWidth}px` : undefined,
                filter: `brightness(${brightnessFilter}) saturate(${saturationFilter})`,
            }}
            loading="eager"
            fetchPriority="high"
        />
    );
};

export default ThemedImage;
